// Shared entities: merge columns of several tables (e.g. every sample_id) into one hub table of
// the integrated model. The build creates the hub table with the union of their values, and
// every member column references it. POST/DELETE .../dbdesign/entities.
// An entity imported from a LinkML schema (LinkmlPanel) has no member at first and may have more
// slots than its key; Find key links proposes columns as its members, accepted or rejected here
// (POST .../dbdesign/mappings/{id}/accept | reject).
import { useState } from "react";
import { Badge, Btn, Field, Input, Select } from "../../components/Shared";
import { Empty } from "../../components/Feedback";
import { SpaceChoice } from "./KeyReview";

// How "different spaces" keeps the studies' keys apart in the build (services/namespacing.py).
const NAMESPACING = { prefix: "study prefix (field-sampling:S001)", composite: "composite key (study_key, key)" };

const mono = { fontFamily: "'JetBrains Mono', monospace", fontSize: 13 };
// Visually hidden: the button's accessible name says which column and entity (Btn takes no aria-label).
const srOnly = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" };

function NewEntity({ tables, busy, onCreate }) {
  const [name, setName] = useState("");
  const [keyName, setKeyName] = useState("");
  const [description, setDescription] = useState("");
  const [picked, setPicked] = useState([]);
  const toggle = id => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

  const submit = async e => {
    e.preventDefault();
    const ok = await onCreate({
      name: name.trim(), key_name: keyName.trim() || null, column_ids: picked,
      description: description.trim() || null,
    });
    if (ok) { setName(""); setKeyName(""); setDescription(""); setPicked([]); }
  };

  return (
    <form onSubmit={submit} aria-label="New shared entity" style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
        <Field label="Entity (table) name" required hint="e.g. sample"><Input value={name} onChange={e => setName(e.target.value)} /></Field>
        <Field label="Key column name" hint="defaults to the first picked column's name">
          <Input value={keyName} onChange={e => setKeyName(e.target.value)} />
        </Field>
        <Field label="Description"><Input value={description} onChange={e => setDescription(e.target.value)} /></Field>
      </div>
      <fieldset style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px", minWidth: 0 }}>
        <legend style={{ fontSize: 12, fontWeight: 600, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Columns to merge ({picked.length})
        </legend>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(280px, 100%), 1fr))", gap: "6px 16px", maxHeight: 220, overflowY: "auto" }}>
          {tables.filter(t => t.include).flatMap(t => t.fields.map(f => (
            <label key={f.column_id} style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 13, minWidth: 0 }}>
              <input type="checkbox" checked={picked.includes(f.column_id)} onChange={() => toggle(f.column_id)}
                     style={{ width: 18, height: 18, margin: 0, flexShrink: 0, accentColor: "var(--dp-accent-dark)" }} />
              <span style={{ ...mono, minWidth: 0, overflowWrap: "anywhere" }}>{t.name}.{f.name}</span>
            </label>
          )))}
        </div>
      </fieldset>
      <div><Btn type="submit" disabled={busy || !name.trim() || picked.length === 0}>Create shared entity</Btn></div>
    </form>
  );
}

function EntitySpace({ e, canManage, busy, onSpace }) {
  const mode = e.namespacing || "prefix";
  const pick = e.identifier_space === "different" ? (
    <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 12 }}>
      Keep apart with
      <span style={{ minWidth: 220 }}>
        <Select aria-label={`How to keep the keys of ${e.name} apart`} options={Object.keys(NAMESPACING)}
                value={mode} disabled={busy}
                onChange={ev => onSpace(e, "different", ev.target.value)} />
      </span>
    </label>
  ) : null;
  return (
    <>
      <SpaceChoice space={e.identifier_space} label={e.name} canManage={canManage} busy={busy}
                   onSpace={space => onSpace(e, space, space === "different" ? mode : null)} extra={pick} />
      {e.identifier_space === "different" && (
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
          The build keeps each study's keys apart: {NAMESPACING[mode]}.
        </div>
      )}
    </>
  );
}

// The slots besides the key: "depth_m (number)", or "site → site" for one whose range is
// another entity (stored only, not built: the build makes the key column).
function slotText(s) {
  if (s.range) return `${s.name} → ${s.range}`;
  return s.type ? `${s.name} (${s.type})` : s.name;
}

function Proposals({ e, canManage, busy, onMember }) {
  const open = (e.proposals || []).filter(p => p.status === "proposed");
  const rejected = (e.proposals || []).length - open.length;
  if (open.length === 0 && rejected === 0) return null;
  return (
    <div style={{ marginTop: 6 }}>
      {open.length > 0 && (
        <ul aria-label={`Proposed members of ${e.name}`} style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {open.map(p => {
            const col = `${p.table}.${p.field}`;
            return (
              <li key={p.mapping_id} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 12, padding: "3px 0" }}>
                <Badge color="amber">proposed</Badge>
                <span style={mono}>{col}</span>
                <span style={{ color: "#4b5563" }}>
                  {p.study}
                  {p.comment && ` · ${p.comment}`}
                </span>
                {canManage && (
                  <>
                    <Btn variant="secondary" disabled={busy}
                         onClick={() => onMember(e, p, "accept")} style={{ padding: "3px 10px", fontSize: 12 }}>
                      Accept<span style={srOnly}> {col} into {e.name}</span>
                    </Btn>
                    <Btn variant="ghost" disabled={busy}
                         onClick={() => onMember(e, p, "reject")} style={{ padding: "3px 10px", fontSize: 12 }}>
                      Reject<span style={srOnly}> {col} for {e.name}</span>
                    </Btn>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {rejected > 0 && (
        <div style={{ fontSize: 12, color: "#6b7280" }}>{rejected} rejected proposal{rejected === 1 ? "" : "s"} (not proposed again).</div>
      )}
    </div>
  );
}

export default function EntitiesPanel({ ws, canManage, busy, onCreate, onDelete, onSpace, onMember }) {
  return (
    <div>
      {ws.entities.length === 0 ? (
        <Empty>No shared entities. Merge columns that mean the same thing across studies (for example every sample_id) into one.</Empty>
      ) : (
        <ul aria-label="Shared entities" style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {ws.entities.map(e => (
            <li key={e.id} style={{ padding: "10px 0", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <strong style={mono}>{e.name}</strong>
                  {e.slots.filter(s => s.is_key).map(s => <Badge key={s.id} color="green">key {s.name}: {s.type}</Badge>)}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                  {e.description && <>{e.description} · </>}
                  merges {e.members.map(m => `${m.table}.${m.field}`).join(", ") || "nothing"}
                </div>
                {e.slots.some(s => !s.is_key) && (
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                    Other slots: {e.slots.filter(s => !s.is_key).map(slotText).join(", ")}
                  </div>
                )}
                <Proposals e={e} canManage={canManage} busy={busy} onMember={onMember} />
                {e.cross_study && <EntitySpace e={e} canManage={canManage} busy={busy} onSpace={onSpace} />}
              </div>
              {canManage && (
                <Btn variant="secondary" disabled={busy} onClick={() => onDelete(e)} style={{ padding: "6px 12px", fontSize: 13 }}>
                  Remove {e.name}
                </Btn>
              )}
            </li>
          ))}
        </ul>
      )}
      {canManage && ws.tables.length > 0 && <NewEntity tables={ws.tables} busy={busy} onCreate={onCreate} />}
    </div>
  );
}
